import React, { useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Print, Close } from "@mui/icons-material";
import LOGO_BASE64 from "../assets/data/logoBase64";

const TransactionReceipt = ({
  open,
  onClose,
  transaction,
  customerDetails,
  accountBalanceData,
  trayTypes = [],
  employerName
}) => {
  const printRef = useRef();

  if (!transaction) return null;

  const {
    sales = [],
    trays = [],
    paymentAmount = 0,
    dateOfTransaction,
  } = transaction;

  // Calculate Sales Total (what customer owes)
  const salesTotal = sales.reduce((sum, sale) => {
    const qty = Number(sale.numberOfItems) || 0;
    const cost = Number(sale.costPerItem) || 0;
    return sum + qty * cost;
  }, 0);

  const totalBoxes = sales.reduce((sum, sale) => {
    return sum + (Number(sale.numberOfItems) || 0);
  }, 0);

  // Calculate Tray Payment (credit for returned trays)
  const trayPaymentTotal = trays.reduce((sum, tray) => {
    if (tray.excludePayment) return sum; // Skip calculation for "Return Only" trays

    const trayType = trayTypes.find((t) => t.trayTypeId === tray.trayTypeId || t.id === tray.trayTypeId) || null;
    const cost = trayType?.price || Number(tray.costPerTray) || 0;
    return sum + (Number(tray.numberOfTrays) * cost);
  }, 0);

  // Payment received (cash)
  const paymentReceived = Number(paymentAmount) || 0;

  // Total credits (payment + tray returns)
  const totalCredits = paymentReceived + trayPaymentTotal;

  // Balance for this transaction
  const transactionBalance = salesTotal - totalCredits;

  // Primary color (MUI default blue)
  const primaryColor = "#1976d2";
  const successColor = "#2e7d32";
  const errorColor = "#d32f2f";

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${customerDetails?.name} - ${formatDate(dateOfTransaction)}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              font-size: 14px;
            }
            .receipt-container {
              border: 1px solid #ccc;
              padding: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
            }
            th, td {
              border: 1px solid #333;
              padding: 8px;
            }
            th {
              background: #f5f5f5;
              font-weight: bold;
            }
            .section-header {
              background: ${primaryColor} !important;
              color: white !important;
              font-weight: bold;
              text-align: center;
            }
            .totals-row {
              background: #f9f9f9;
              font-weight: bold;
            }
            .credit-row {
              background: #e8f5e9;
            }
            .credit-value {
              color: ${successColor};
              font-weight: bold;
            }
            .final-row {
              background: #e3f2fd;
              font-weight: bold;
              font-size: 16px;
            }
            @media print {
              body { padding: 10px; }
              .receipt-container { border: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getTrayTypeName = (trayTypeId) => {
    const tray =
      trayTypes.find(
        (t) => t.trayTypeId === trayTypeId || t.id === trayTypeId
      ) || null;
    return tray?.label || "Tray";
  };

  const getTrayTypePrice = (trayTypeId) => {
    const tray =
      trayTypes.find(
        (t) => t.trayTypeId === trayTypeId || t.id === trayTypeId
      ) || null;
    return tray?.price || 0;
  };

  // Account balance from passed data
  const accountBalance = accountBalanceData?.totalAmountDue || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#f5f5f5",
          borderBottom: `2px solid ${primaryColor}`,
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="primary">
          Transaction Receipt
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: "#fafafa" }}>
        <Box ref={printRef} sx={{ p: 3, bgcolor: "#fff" }}>
          <div className="receipt-container">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 20,
                paddingBottom: 15,
                borderBottom: `2px solid ${primaryColor}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <img
                  src={LOGO_BASE64}
                  alt="DBF Logo"
                  style={{ width: 50, height: 50, borderRadius: 4 }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: "bold",
                      color: primaryColor,
                    }}
                  >
                    {employerName}
                  </div>
                  <div style={{ color: "#666", fontSize: 16 }}>
                    {customerDetails?.branchName || "Transaction Receipt"}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    color: primaryColor,
                    fontWeight: "medium",
                    fontSize: 14,
                  }}
                >
                  <span style={{ color: "#ddd" }}>Date:</span>{" "}
                  {formatDate(dateOfTransaction)}
                </div>
              </div>
            </div>

            {/* Customer Section */}
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: "bold",
                  color: primaryColor,
                  marginBottom: 0,
                  textTransform: "capitalize",
                }}
              >
                {customerDetails?.name || "N/A"}
              </div>
              <div style={{ textTransform: "capitalize", color: "#666" }}>
                {customerDetails?.location || "N/A"}
              </div>
            </div>

            {/* SALES SECTION */}
            {sales.length > 0 && (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: 15,
                }}
              >
                <thead>
                  <tr>
                    <th
                      colSpan={5}
                      style={{
                        background: primaryColor,
                        color: "white",
                        textAlign: "center",
                        padding: 10,
                        border: "1px solid #333",
                      }}
                    >
                      Sales
                    </th>
                  </tr>
                  <tr>
                    <th
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        background: "#f5f5f5",
                        width: 40,
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        background: "#f5f5f5",
                      }}
                    >
                      Item
                    </th>
                    <th
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        background: "#f5f5f5",
                        textAlign: "right",
                        width: 80,
                      }}
                    >
                      Qty
                    </th>
                    <th
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        background: "#f5f5f5",
                        textAlign: "right",
                        width: 100,
                      }}
                    >
                      Rate
                    </th>
                    <th
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        background: "#f5f5f5",
                        textAlign: "right",
                        width: 120,
                      }}
                    >
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale, idx) => {
                    const qty = Number(sale.numberOfItems) || 0;
                    const rate = Number(sale.costPerItem) || 0;
                    const amount = qty * rate;
                    return (
                      <tr key={idx}>
                        <td
                          style={{
                            border: "1px solid #333",
                            padding: 8,
                            textAlign: "center",
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td style={{ border: "1px solid #333", padding: 8 }}>
                          {sale.itemName}
                        </td>
                        <td
                          style={{
                            border: "1px solid #333",
                            padding: 8,
                            textAlign: "right",
                          }}
                        >
                          {qty}
                        </td>
                        <td
                          style={{
                            border: "1px solid #333",
                            padding: 8,
                            textAlign: "right",
                          }}
                        >
                          ₹{formatCurrency(rate)}
                        </td>
                        <td
                          style={{
                            border: "1px solid #333",
                            padding: 8,
                            textAlign: "right",
                          }}
                        >
                          ₹{formatCurrency(amount)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        fontWeight: "bold",
                        background: "#f9f9f9",
                        textAlign: "right",
                      }}
                    >
                      Total:
                    </td>
                    {/* <td style={{ border: "1px solid #333", padding: 8, fontWeight: "bold", background: "#f9f9f9", textAlign: "right" }}>{totalBoxes}</td> */}
                    {/* <td style={{ border: "1px solid #333", padding: 8, background: "#f9f9f9" }}></td> */}
                    <td
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        fontWeight: "bold",
                        background: "#f9f9f9",
                        textAlign: "right",
                      }}
                    >
                      ₹{formatCurrency(salesTotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            {/* PAYMENT AND TRAY RETURNS SECTION */}
            {(paymentReceived > 0 || trays.length > 0) && (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: 15,
                }}
              >
                <thead>
                  <tr>
                    <th
                      colSpan={4}
                      style={{
                        background: successColor,
                        color: "white",
                        textAlign: "center",
                        padding: 10,
                        border: "1px solid #333",
                      }}
                    >
                      Payment and Tray Returns
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Payment Received */}
                  {paymentReceived > 0 && (
                    <tr style={{ background: "#e8f5e9" }}>
                      <td
                        colSpan={3}
                        style={{
                          border: "1px solid #333",
                          padding: 8,
                          fontWeight: "bold",
                        }}
                      >
                        Cash/Online
                      </td>
                      <td
                        style={{
                          border: "1px solid #333",
                          padding: 8,
                          textAlign: "right",
                          color: successColor,
                          fontWeight: "bold",
                        }}
                      >
                        ₹{formatCurrency(paymentReceived)}
                      </td>
                    </tr>
                  )}

                  {/* Tray Returns */}
                  {trays.map((tray, idx) => {
                    const qty = Number(tray.numberOfTrays) || 0;
                    const price = getTrayTypePrice(tray.trayTypeId);
                    const total = qty * price;
                    return (
                      <tr key={idx} style={{ background: "#e8f5e9" }}>
                        <td
                          style={{
                            border: "1px solid #333",
                            padding: 8,
                            fontWeight: "bold",
                          }}
                        >
                          Tray Returned
                        </td>
                        <td style={{ border: "1px solid #333", padding: 8 }}>
                          {getTrayTypeName(tray.trayTypeId)}
                        </td>
                        <td
                          style={{
                            border: "1px solid #333",
                            padding: 8,
                            textAlign: "center",
                          }}
                        >
                          {qty} × ₹{tray.excludePayment ? (0 + '(Payment Excluded)') : price}
                        </td>
                        <td
                          style={{
                            border: "1px solid #333",
                            padding: 8,
                            textAlign: "right",
                            color: successColor,
                            fontWeight: "bold",
                          }}
                        >
                          ₹{tray.excludePayment ? 0 : formatCurrency(total)}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Total Credits */}
                  <tr style={{ background: "#c8e6c9", fontWeight: "bold" }}>
                    <td
                      colSpan={3}
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        fontWeight: "bold",
                        textAlign: "right",
                      }}
                    >
                      Total Payment Received:
                    </td>
                    <td
                      style={{
                        border: "1px solid #333",
                        padding: 8,
                        textAlign: "right",
                        color: successColor,
                        fontWeight: "bold",
                        fontSize: 16,
                      }}
                    >
                      ₹{formatCurrency(totalCredits)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            {/* SUMMARY */}
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 15,
              }}
            >
              <tbody>
                <tr style={{ background: "#f5f5f5" }}>
                  <td
                    style={{
                      border: "1px solid #333",
                      padding: 10,
                      fontWeight: "bold",
                    }}
                  >
                    Sales Total
                  </td>
                  <td
                    style={{
                      border: "1px solid #333",
                      padding: 10,
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    ₹{formatCurrency(salesTotal)}
                  </td>
                </tr>
                {totalCredits > 0 && (
                  <tr style={{ background: "#e8f5e9" }}>
                    <td
                      style={{
                        border: "1px solid #333",
                        padding: 10,
                        fontWeight: "bold",
                      }}
                    >
                      Total Credits
                    </td>
                    <td
                      style={{
                        border: "1px solid #333",
                        padding: 10,
                        textAlign: "right",
                        fontWeight: "bold",
                        color: successColor,
                      }}
                    >
                      - ₹{formatCurrency(totalCredits)}
                    </td>
                  </tr>
                )}
                <tr style={{ background: "#e3f2fd" }}>
                  <td
                    style={{
                      border: "1px solid #333",
                      padding: 12,
                      fontWeight: "bold",
                      fontSize: 16,
                    }}
                  >
                    {transactionBalance >= 0 ? "Balance Due" : "Credit Balance"}
                  </td>
                  <td
                    style={{
                      border: "1px solid #333",
                      padding: 12,
                      textAlign: "right",
                      fontWeight: "bold",
                      fontSize: 18,
                      color:
                        transactionBalance >= 0 ? errorColor : successColor,
                    }}
                  >
                    ₹{formatCurrency(Math.abs(transactionBalance))}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Account Balance */}
            <div
              style={{
                textAlign: "center",
                marginTop: 20,
                paddingTop: 15,
                borderTop: "2px dashed #ccc",
                fontSize: 18,
              }}
            >
              <span style={{ fontWeight: "bold" }}>
                Overall Account Balance:
              </span>
              <span
                style={{
                  fontWeight: "bold",
                  marginLeft: 10,
                  fontSize: 16,
                  color: accountBalance > 0 ? errorColor : successColor,
                }}
              >
                ₹{formatCurrency(Math.abs(accountBalance))}{" "}
                {accountBalance > 0 ? "(Due)" : "(Credit)"}
              </span>
            </div>
          </div>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, bgcolor: "#f5f5f5", borderTop: "1px solid #e0e0e0" }}
      >
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button onClick={handlePrint} variant="contained" startIcon={<Print />}>
          Print Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionReceipt;
